import { DeepFilterNet3Processor } from 'deepfilternet3-noise-filter';
import { INoiseProcessor, AudioQualityMetrics, DEFAULT_SUPPRESSION_LEVEL } from './types';

/**
 * DeepFilterProcessor
 *
 * Modes:
 * 1. Pure (Default): Minimal wrapper around DeepFilterNet. Best fidelity.
 * 2. Smart (Enhanced): Adds Pre-Gain, EQ, Compressor, and Soft Gate.
 *
 * Configuration:
 * - Sample Rate: 48kHz
 * - Noise Reduction: 100 (Max)
 */
export class DeepFilterProcessor implements INoiseProcessor {
  private audioContext: AudioContext | null = null;
  private deepFilterNet3: DeepFilterNet3Processor | null = null;
  private deepFilterWorkletNode: AudioWorkletNode | null = null;

  // Audio Graph Nodes
  private destinationNode: MediaStreamAudioDestinationNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;

  // Smart Mixing Nodes
  private preGainNode: GainNode | null = null;
  private eqNode: BiquadFilterNode | null = null;
  private compressorNode: DynamicsCompressorNode | null = null;
  private gateWorkletNode: AudioWorkletNode | null = null;
  private isGateWorkletLoaded: boolean = false;

  // Passive VAD (Dynamic Suppression Logic)
  private vadAnalyser: AnalyserNode | null = null;
  private vadWorker: Worker | null = null;

  // CRITICAL AUDIO QUALITY PARAMETERS - CENTRALIZED CONFIGURATION
  private readonly AUDIO_QUALITY_CONFIG = {
    // Dynamic suppression parameters - TUNED TO PREVENT WORD CUT-OFF
    ATTACK_STEP: 4, // Slower attack (was 8) - prevents cutting off speech onset
    RELEASE_STEP: 2, // Gentler release (was 4) - smoother transitions after speech
    HOLD_DURATION: 1200, // Longer hold (was 800) - bridges gaps during speech with noise
    MIN_SUPPRESSION: 40, // Higher floor prevents over-aggressive reduction
    MAX_SUPPRESSION: 80, // Preserves voice clarity

    // Gate timing for natural speech - IMPROVED DETECTION
    GATE_ATTACK_TIME: 0.01, // 10ms attack (was 20ms) - faster opening for word clarity
    GATE_RELEASE_TIME: 0.5, // 500ms release - smoother closing
    GATE_HOLD_TIME: 0.5, // 500ms hold - respects natural speech rhythm
    GATE_FLOOR_GAIN: 0.15, // Floor gain when gate is closed
    GATE_THRESHOLD: 5, // Frequency average threshold to detect speech

    // Signal processing chain parameters
    POST_GAIN_FACTOR: 1.15, // Modest boost (+1.2dB)
    COMPRESSOR_THRESHOLD: -12, // Conservative compression threshold
    COMPRESSOR_RATIO: 2.0, // Moderate compression ratio
    COMPRESSOR_ATTACK: 0.02, // 20ms attack time
    COMPRESSOR_RELEASE: 0.4, // 400ms release time
    COMPRESSOR_KNEE: 20, // Sharper knee for cleaner transitions

    // EQ parameters for presence boost
    EQ_FREQUENCY: 5000, // 5kHz high-shelf
    EQ_GAIN: 3.0, // +3dB presence boost

    // VAD sensitivity parameters - TUNED FOR SPEECH WITH BACKGROUND NOISE
    SPEECH_SUPPRESSION_LEVEL: 35, // Higher level (was 20) - more preservation during speech
    VAD_CONFIDENCE_THRESHOLD: 0.15, // Lower threshold (was 0.25) - more sensitive detection
    SUPPRESSION_THRESHOLD: 0.2, // Lower threshold (was 0.25) - easier speech detection
  };

  // Post-processing for Pure Mode
  private postGainNode: GainNode | null = null;

  // State
  private smartMixingEnabled: boolean = false;
  private dynamicSuppressionEnabled: boolean = true;
  private suppressionLevel: number = DEFAULT_SUPPRESSION_LEVEL;

  private metricsCallbacks: Set<(metrics: Partial<AudioQualityMetrics>) => void> = new Set();
  private isProcessing: boolean = false;
  private isMuted: boolean = false;
  private currentStream: MediaStream | null = null;

  // Individual Smart Mixing toggles (for testing)
  private preGainEnabled: boolean = true;
  private eqEnabled: boolean = true;
  private compressorEnabled: boolean = true;
  private gateEnabled: boolean = true;
  private postGainEnabled: boolean = true;

  constructor() {}

  async init(context: AudioContext): Promise<void> {
    this.audioContext = context;

    // Ensure 48kHz if possible, though context is already created
    if (this.audioContext.sampleRate !== 48000) {
      console.warn(
        '[DeepFilterProcessor] AudioContext sample rate is not 48000Hz. Audio quality may be suboptimal.',
        this.audioContext.sampleRate
      );
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Load SmartGateWorklet if not already loaded
    if (!this.isGateWorkletLoaded) {
      try {
        await this.audioContext.audioWorklet.addModule(`${process.env.PUBLIC_URL}/static/js/SmartGateWorklet.js`);
        this.isGateWorkletLoaded = true;
        console.log('[DeepFilterProcessor] SmartGateWorklet loaded');
      } catch (e) {
        console.warn('[DeepFilterProcessor] Failed to load SmartGateWorklet:', e);
      }
    }

    const t0 = performance.now();
    console.log('[DeepFilterProcessor] Initializing Pure DeepFilterNet3 with optimized parameters...');
    console.log(`[DeepFilterProcessor] Audio Quality Config:`, this.AUDIO_QUALITY_CONFIG);

    this.destinationNode = this.audioContext.createMediaStreamDestination();
    this.destinationNode.channelCount = 1;
    this.destinationNode.channelCountMode = 'explicit';

    // Initialize the lower-level processor directly
    try {
      this.deepFilterNet3 = new DeepFilterNet3Processor({
        sampleRate: 48000,
        noiseReductionLevel: DEFAULT_SUPPRESSION_LEVEL,
        assetConfig: {
          cdnUrl: window.location.origin + process.env.PUBLIC_URL + '/static/wasm',
        },
      });

      await this.deepFilterNet3.initialize();

      // Create the AudioWorkletNode on OUR context
      // STRATEGY: Try to instantiate the node directly first.
      // If the processor is already registered on this context (from previous session), this succeeds.
      // If it fails (e.g. processor not found), we catch it and use the library method to register it.
      try {
        const assets = (this.deepFilterNet3 as any).assets;
        if (!assets) throw new Error('DeepFilter assets missing during manual node creation');

        // Optimistically try to create the node, assuming worklet is registered
        this.deepFilterWorkletNode = new AudioWorkletNode(this.audioContext, 'deepfilter-audio-processor', {
          processorOptions: {
            wasmModule: assets.wasmModule,
            modelBytes: assets.modelBytes,
            suppressionLevel: (this.deepFilterNet3 as any).config.noiseReductionLevel,
          },
        });

        console.log('[DeepFilterProcessor] Reused existing worklet registration.');
      } catch (error: any) {
        if (error.name === 'InvalidStateError' || error.message?.includes('not defined')) {
          console.log('[DeepFilterProcessor] Worklet not yet registered. Registering now...');
        } else {
          console.warn('[DeepFilterProcessor] Worklet instantiation failed, attempting registration fallback:', error);
        }

        // Fallback: Use library method to register the worklet and create the node
        try {
          this.deepFilterWorkletNode = await this.deepFilterNet3.createAudioWorkletNode(this.audioContext);
        } catch (e) {
          // If this also fails, maybe it WAS registered but instantiation failed differently?
          // Or maybe registration failed?
          console.error('[DeepFilterProcessor] Fatal error registering/creating worklet:', e);
          throw e;
        }
      }

      // Important: Link manual node back to processor instance so methods work
      if (this.deepFilterWorkletNode) {
        (this.deepFilterNet3 as any).workletNode = this.deepFilterWorkletNode;
      }

      console.log(`[DeepFilterProcessor] Init completed in ${(performance.now() - t0).toFixed(1)}ms`);
    } catch (err) {
      console.error('[DeepFilterProcessor] Failed to initialize DeepFilterNet3:', err);
      throw err;
    }
  }

  async process(stream: MediaStream): Promise<MediaStream> {
    if (!this.deepFilterNet3 || !this.deepFilterWorkletNode || !this.audioContext) {
      throw new Error('DeepFilterProcessor not initialized');
    }

    this.currentStream = stream;
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) {
      throw new Error('No audio track in stream');
    }

    // 1. Create persistent internal nodes if they don't exist OR if previous output track is dead
    const outputTrack = this.destinationNode?.stream?.getAudioTracks()[0];
    const isOutputEnded = outputTrack && outputTrack.readyState === 'ended';

    if (!this.destinationNode || isOutputEnded) {
      if (this.destinationNode) {
        // Disconnect old invalid node if it exists
        try {
          this.destinationNode.disconnect();
        } catch (e) {}
      }
      this.destinationNode = this.audioContext.createMediaStreamDestination();
      this.destinationNode.channelCount = 1;
      this.destinationNode.channelCountMode = 'explicit';
    }

    // 2. Initial Setup: Start in the configured mode (Smart or Pure)
    await this.setupAudioGraph(audioTrack);

    this.isProcessing = true;

    // Always return the persistent destination stream
    return this.destinationNode.stream;
  }

  /**
   * Rebuilds the audio graph based on the current mode (Smart vs Pure).
   */
  private async setupAudioGraph(inputTrack: MediaStreamTrack): Promise<void> {
    if (!this.audioContext || !this.destinationNode || !this.deepFilterWorkletNode) return;
    const t0 = performance.now();

    // cleanup previous routing
    this.disconnectNodes();

    // Create source from input
    this.sourceNode = this.audioContext.createMediaStreamSource(new MediaStream([inputTrack]));

    if (this.smartMixingEnabled) {
      // --- SMART MIXING MODE ---
      // Chain: Source -> PreGain -> DeepFilterWorkletNode -> EQ -> Compressor -> PostGain -> SmartGate -> Destination
      console.log('[DeepFilterProcessor] Building Smart Mixing Graph...');

      // 1. Pre-Processing (Pre-Gain)
      this.ensurePreGainNode();
      this.sourceNode.connect(this.preGainNode!);

      // Pipe PreGain output into DeepFilter Worklet Node
      this.preGainNode!.connect(this.deepFilterWorkletNode);

      // Re-apply stored suppression level
      this.setSuppressionLevel(this.suppressionLevel);

      // 2. Post-Processing (EQ -> Comp -> Gate)
      // Connect DeepFilter Output -> EQ
      this.ensureEQNode();
      this.ensureCompressorNode();
      this.ensurePostGainNode();
      this.ensureGateWorkletNode();

      // DeepFilter -> EQ
      this.deepFilterWorkletNode.connect(this.eqNode!);
      // EQ -> Compressor
      this.eqNode!.connect(this.compressorNode!);
      // Compressor -> PostGain
      this.compressorNode!.connect(this.postGainNode!);

      if (this.gateWorkletNode) {
        // PostGain -> Gate -> Destination
        this.postGainNode!.connect(this.gateWorkletNode);
        this.gateWorkletNode.connect(this.destinationNode);
      } else {
        // Fallback
        this.postGainNode!.connect(this.destinationNode);
      }

      // Connect Passive VAD (Dynamic Suppression)
      if (this.dynamicSuppressionEnabled) {
        this.ensureVadNode();
        // Tap analysis from PostGain (cleanest signal)
        this.postGainNode!.connect(this.vadAnalyser!);
        if (!this.isMuted) {
          this.startVadLogic();
        }
      }
    } else {
      // --- PURE MODE (TRUE BYPASS) ---
      // Chain: Source -> DeepFilterWorkletNode -> Destination
      console.log('[DeepFilterProcessor] Building Pure Mode Graph (True Bypass)...');

      // Connect Source -> DeepFilterWorkletNode
      this.sourceNode.connect(this.deepFilterWorkletNode);

      // Re-apply stored suppression level
      this.setSuppressionLevel(this.suppressionLevel);

      // Connect DeepFilterWorkletNode -> Destination
      this.deepFilterWorkletNode.connect(this.destinationNode);

      // Connect Passive VAD (Dynamic Suppression)
      if (this.dynamicSuppressionEnabled) {
        this.ensureVadNode();
        // Tap analysis from DeepFilter output
        this.deepFilterWorkletNode.connect(this.vadAnalyser!);
        if (!this.isMuted) {
          this.startVadLogic();
        }
      }
    }
    console.log(
      `[DeepFilterProcessor] setupAudioGraph completed in ${(performance.now() - t0).toFixed(1)}ms (mode=${this.smartMixingEnabled ? 'Smart' : 'Pure'})`
    );
  }

  // --- Modular Enhancements ---

  private ensurePreGainNode(): void {
    if (!this.audioContext || this.preGainNode) return;
    this.preGainNode = this.audioContext.createGain();
    // Tuning: Reset to 1.0 (Neutral). We will boost at PostGain instead.
    this.preGainNode.gain.value = 1.0;
  }

  private ensurePostGainNode(): void {
    if (!this.audioContext) return;
    if (!this.postGainNode) {
      this.postGainNode = this.audioContext.createGain();
    }
    // Modest boost to compensate for DeepFilter's output level - using config value
    this.postGainNode.gain.value = this.AUDIO_QUALITY_CONFIG.POST_GAIN_FACTOR;
  }

  private ensureEQNode(): void {
    if (!this.audioContext || this.eqNode) return;
    this.eqNode = this.audioContext.createBiquadFilter();
    this.eqNode.type = 'highshelf';
    // Using config values for EQ
    this.eqNode.frequency.value = this.AUDIO_QUALITY_CONFIG.EQ_FREQUENCY;
    this.eqNode.gain.value = this.AUDIO_QUALITY_CONFIG.EQ_GAIN;
  }

  private ensureCompressorNode(): void {
    if (!this.audioContext || this.compressorNode) return;
    this.compressorNode = this.audioContext.createDynamicsCompressor();
    // USING CENTRALIZED AUDIO QUALITY CONFIG
    this.compressorNode.threshold.value = this.AUDIO_QUALITY_CONFIG.COMPRESSOR_THRESHOLD;
    this.compressorNode.knee.value = this.AUDIO_QUALITY_CONFIG.COMPRESSOR_KNEE;
    this.compressorNode.ratio.value = this.AUDIO_QUALITY_CONFIG.COMPRESSOR_RATIO;
    this.compressorNode.attack.value = this.AUDIO_QUALITY_CONFIG.COMPRESSOR_ATTACK;
    this.compressorNode.release.value = this.AUDIO_QUALITY_CONFIG.COMPRESSOR_RELEASE;
  }

  private ensureGateWorkletNode(): void {
    if (!this.audioContext || !this.isGateWorkletLoaded) return;
    if (this.gateWorkletNode) return;

    try {
      this.gateWorkletNode = new AudioWorkletNode(this.audioContext, 'SmartGateWorklet', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });

      // Apply initial params from config
      this.updateGateParams();

      this.gateWorkletNode.onprocessorerror = (err) => {
        console.error('[DeepFilterProcessor] SmartGateWorklet error:', err);
      };
    } catch (e) {
      console.error('[DeepFilterProcessor] Failed to create SmartGateWorkletNode:', e);
    }
  }

  private updateGateParams(): void {
    if (!this.gateWorkletNode) return;

    const params = this.gateWorkletNode.parameters;
    const config = this.AUDIO_QUALITY_CONFIG;

    // Map config to AudioParams
    const setParam = (name: string, value: number) => {
      // Cast to any to avoid TS error: Property 'get' does not exist on type 'AudioParamMap'
      const param = (params as any).get(name);
      if (param) param.setValueAtTime(value, this.audioContext!.currentTime);
    };

    setParam('threshold', config.GATE_THRESHOLD / 255); // Use config threshold (byte range approx)
    setParam('attack', config.GATE_ATTACK_TIME);
    setParam('release', config.GATE_RELEASE_TIME);
    setParam('hold', config.GATE_HOLD_TIME);
    setParam('floor', config.GATE_FLOOR_GAIN);
  }

  private ensureVadNode(): void {
    if (!this.audioContext) return;
    if (!this.vadAnalyser) {
      this.vadAnalyser = this.audioContext.createAnalyser();
      this.vadAnalyser.fftSize = 512;
      this.vadAnalyser.smoothingTimeConstant = 0.4;
    }
  }

  private startVadLogic(): void {
    this.stopVadLogic();
    const workerBlob = new Blob(
      [
        `
      let intervalId = null;
      let holdTimer = 0;
      let currentSuppression = 100;
      let lastPostedLevel = -1;
      let lastPostedVad = -1;
      
      const TICK_RATE = 50; // ms
      const HOLD_DURATION = ${this.AUDIO_QUALITY_CONFIG.HOLD_DURATION}; // Extended hold time
      
      // Dynamic State - TUNED FOR NATURAL SPEECH PATTERNS
      let targetSpeechSuppression = ${this.AUDIO_QUALITY_CONFIG.SPEECH_SUPPRESSION_LEVEL};
      let targetNoiseSuppression = ${this.AUDIO_QUALITY_CONFIG.MAX_SUPPRESSION};
      const MIN_SUPPRESSION = ${this.AUDIO_QUALITY_CONFIG.MIN_SUPPRESSION}; // Higher attenuation floor
      
      // CRITICAL: Tuned attack/release for natural transitions
      // ATTACK_STEP: Slower attack prevents word cut-offs during speech onset
      // RELEASE_STEP: Gentler release maintains continuity during speech pauses
      const ATTACK_STEP = ${this.AUDIO_QUALITY_CONFIG.ATTACK_STEP}; 
      const RELEASE_STEP = ${this.AUDIO_QUALITY_CONFIG.RELEASE_STEP}; // Slower release = smoother transitions 
      const SPEECH_THRESHOLD = ${this.AUDIO_QUALITY_CONFIG.SUPPRESSION_THRESHOLD}; // Configurable speech detection threshold

      let isDynamicMode = true; // Default ON

      self.onmessage = function(e) {
        if (e.data === 'start') {
          if (intervalId) clearInterval(intervalId);
          intervalId = setInterval(() => { self.postMessage('tick'); }, TICK_RATE);
        } else if (e.data === 'stop') {
          if (intervalId) clearInterval(intervalId);
          intervalId = null;
        } else if (e.data.type === 'SET_PARAMS') {
           if (e.data.maxLevel !== undefined) targetNoiseSuppression = e.data.maxLevel;
           if (e.data.speechLevel !== undefined) targetSpeechSuppression = e.data.speechLevel;
        } else if (e.data.type === 'SET_MODE') {
           if (e.data.dynamicEnabled !== undefined) isDynamicMode = e.data.dynamicEnabled;
        } else if (e.data.type === 'update') {
           const vadLevel = e.data.vad; 
           const isSpeaking = vadLevel > SPEECH_THRESHOLD; // Use configurable threshold 
           
           let target = targetNoiseSuppression;

          // Dynamic Logic Calculation
           if (target > 50) {
              if (isSpeaking) {
                target = Math.min(targetSpeechSuppression, targetNoiseSuppression); 
                holdTimer = HOLD_DURATION;
              } else {
                if (holdTimer > 0) {
                  target = Math.min(targetSpeechSuppression, targetNoiseSuppression);
                  holdTimer -= TICK_RATE;
                } else {
                  target = targetNoiseSuppression;
                }
              }
           }

           if (currentSuppression > target) {
             currentSuppression = Math.max(target, currentSuppression - ATTACK_STEP);
           } else if (currentSuppression < target) {
             currentSuppression = Math.min(target, currentSuppression + RELEASE_STEP);
           }

           // Final Output Decision
           // If Dynamic: Use calculated smoothed envelope
           // If Static: Use targetNoiseSuppression (Manual Slider)
           // Apply MIN_SUPPRESSION floor to prevent robotic voice from over-attenuation
           const clampedSuppression = Math.max(currentSuppression, MIN_SUPPRESSION);
           const finalLevel = isDynamicMode ? clampedSuppression : Math.max(targetNoiseSuppression, MIN_SUPPRESSION);

           // OPTIMIZATION: Dirty checking to reduce main thread traffic
           // Only post if levels changed significantly
           if (Math.abs(finalLevel - lastPostedLevel) > 1 || Math.abs(vadLevel - lastPostedVad) > 0.05) {
               self.postMessage({ type: 'suppression', level: finalLevel, vad: vadLevel });
               lastPostedLevel = finalLevel;
               lastPostedVad = vadLevel;
           }
        }
      };
    `,
      ],
      { type: 'application/javascript' }
    );

    const workerUrl = URL.createObjectURL(workerBlob);
    this.vadWorker = new Worker(workerUrl);
    URL.revokeObjectURL(workerUrl); // Clean up immediately

    if (!this.vadAnalyser) {
      console.warn('startVadLogic: VAD Analyser missing');
      return;
    }

    const dataArray = new Uint8Array(this.vadAnalyser.frequencyBinCount);

    this.vadWorker.onmessage = (e) => {
      if (e.data === 'tick' && this.vadAnalyser) {
        this.vadAnalyser.getByteFrequencyData(dataArray);
        // Focus on voice frequency bands (bins 3-20 at 512 FFT / 48kHz ≈ 280Hz-3kHz)
        // This reduces false positives from keyboard clicks which have high-frequency content
        let voiceSum = 0;
        const voiceStartBin = 3; // ~280Hz
        const voiceEndBin = Math.min(20, dataArray.length); // ~1.9kHz
        for (let i = voiceStartBin; i < voiceEndBin; i++) voiceSum += dataArray[i];
        const voiceAverage = voiceSum / (voiceEndBin - voiceStartBin);
        // Less aggressive normalization (was /40, now /60) to reduce sensitivity to transients
        const normalized = Math.min(1, voiceAverage / 60);
        this.vadWorker?.postMessage({ type: 'update', vad: normalized });
      } else if (e.data.type === 'suppression') {
        this.applyInternalSuppression(e.data.level);

        if (this.smartMixingEnabled || this.dynamicSuppressionEnabled) {
          this.emitMetrics({ effectiveSuppressionLevel: Math.round(e.data.level) });
        }
      }
    };
    this.vadWorker.postMessage('start');

    this.vadWorker.postMessage({
      type: 'SET_MODE',
      dynamicEnabled: this.dynamicSuppressionEnabled,
    });
  }

  private applyInternalSuppression(level: number) {
    if (!this.deepFilterNet3) return;
    // Clamp to valid range
    const clampedLevel = Math.max(0, Math.min(100, Math.round(level)));
    try {
      if (typeof this.deepFilterNet3.setSuppressionLevel === 'function') {
        this.deepFilterNet3.setSuppressionLevel(clampedLevel);
      }
    } catch (e) {
      console.warn('[DeepFilterProcessor] Failed to set noise reduction level:', e);
    }
  }

  private emitMetrics(metrics: Partial<AudioQualityMetrics>) {
    // DISABLING METRICS to reduce main thread load
    // this.metricsCallbacks.forEach((cb) => cb(metrics));
  }

  private stopVadLogic(): void {
    if (this.vadWorker) {
      this.vadWorker.postMessage('stop');
      this.vadWorker.terminate();
      this.vadWorker = null;
    }
  }

  private disconnectNodes(): void {
    // Stop Logic
    this.stopVadLogic();

    // Disconnect pure/mixed sources
    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
      } catch (e) {}
      this.sourceNode = null;
    }

    // Disconnect Enhancements
    if (this.preGainNode) {
      try {
        this.preGainNode.disconnect();
      } catch (e) {}
      this.preGainNode = null;
    }
    if (this.eqNode) {
      try {
        this.eqNode.disconnect();
      } catch (e) {}
      this.eqNode = null;
    }
    if (this.compressorNode) {
      try {
        this.compressorNode.disconnect();
      } catch (e) {}
      this.compressorNode = null;
    }
    if (this.gateWorkletNode) {
      try {
        this.gateWorkletNode.disconnect();
      } catch (e) {}
      this.gateWorkletNode = null;
    }
    // DO NOT disconnect deepFilterWorkletNode here as it is persistent.
    // We only disconnect it from specific outputs in step 2 logic.
    if (this.deepFilterWorkletNode) {
      try {
        this.deepFilterWorkletNode.disconnect();
      } catch (e) {}
    }

    if (this.vadAnalyser) {
      try {
        this.vadAnalyser.disconnect();
      } catch (e) {}
      this.vadAnalyser = null;
    }
    if (this.postGainNode) {
      try {
        this.postGainNode.disconnect();
      } catch (e) {}
      this.postGainNode = null;
    }
  }

  async setMuted(muted: boolean): Promise<void> {
    this.isMuted = muted;
    // With DeepFilterNet3Processor, we don't have a simple suspend/resume on the class itself
    // IF it relied on context suspension.
    // Since we share the context, we rely on disconnecting nodes.

    const t0 = performance.now();

    if (muted) {
      // === AGGRESSIVE RESOURCE RELEASE ===
      // 1. Stop workers
      this.stopVadLogic();

      // 2. Disconnect source node (Stops input to graph)
      if (this.sourceNode) {
        try {
          this.sourceNode.disconnect();
        } catch (e) {}
      }

      // 3. Disconnect DeepFilter worklet output (Stops output from graph)
      if (this.deepFilterWorkletNode) {
        try {
          this.deepFilterWorkletNode.disconnect();
        } catch (e) {}
      }

      // 4. Disconnect analyser nodes
      if (this.vadAnalyser) {
        try {
          this.vadAnalyser.disconnect();
        } catch (e) {}
      }

      // 5. Explicitly bypass gate to prevent residual noise if it was open
      if (this.gateWorkletNode && this.audioContext) {
        const bypassParam = (this.gateWorkletNode.parameters as any).get('bypass');
        if (bypassParam) bypassParam.setValueAtTime(1, this.audioContext.currentTime);
      }

      console.log(`[DeepFilterProcessor] Muted — nodes disconnected in ${(performance.now() - t0).toFixed(1)}ms`);
    } else {
      // === RESTORE CONNECTIONS ===

      // 1. Reset Gate params (ensure it starts fresh)
      if (this.gateWorkletNode && this.smartMixingEnabled) {
        this.updateGateParams();
        // Restore enabled state
        const bypassParam = (this.gateWorkletNode.parameters as any).get('bypass');
        const shouldBypass = !this.gateEnabled;
        if (bypassParam) bypassParam.setValueAtTime(shouldBypass ? 1 : 0, this.audioContext!.currentTime);
      }

      // Reconnect Source
      if (this.sourceNode) {
        // Reconnect logic depends on Smart vs Pure
        try {
          if (this.smartMixingEnabled && this.preGainNode) {
            this.sourceNode.connect(this.preGainNode);
            // Ensure preGain connects to DF input
            // this.preGainNode.connect(this.deepFilterWorkletNode!); // Should already be connected if we didn't disconnect it?
            // Wait, disconnectNodes() disconnects everything. setMuted() should just break the input/output?
            // disconnectNodes DESTROYS nodes (nulls them). setMuted just disconnects functionality.

            // Let's verify what setMuted disconnects.
            // It disconnects sourceNode.
            // It disconnects deepFilterWorkletNode.

            // So now we must reconnect them.
            if (this.deepFilterWorkletNode && this.preGainNode) {
              this.preGainNode.connect(this.deepFilterWorkletNode);
            }
          } else if (!this.smartMixingEnabled && this.deepFilterWorkletNode) {
            this.sourceNode.connect(this.deepFilterWorkletNode);
          }
        } catch (e) {
          console.warn('[DeepFilterProcessor] Source reconnection error:', e);
        }
      }

      // Reconnect DeepFilter Output
      if (this.deepFilterWorkletNode) {
        try {
          if (this.smartMixingEnabled && this.eqNode) {
            this.deepFilterWorkletNode.connect(this.eqNode);
          } else if (!this.smartMixingEnabled && this.destinationNode) {
            this.deepFilterWorkletNode.connect(this.destinationNode);
          }

          // Reconnect VAD tap
          if (this.dynamicSuppressionEnabled && this.vadAnalyser) {
            if (this.smartMixingEnabled && this.postGainNode) {
              // Tap PostGain in Smart Mode (already connected?)
              // If we disconnected vadAnalyser, we need to reconnect.
              this.postGainNode.connect(this.vadAnalyser);
            } else {
              // Tap DF in Pure Mode
              this.deepFilterWorkletNode.connect(this.vadAnalyser);
            }
          }
        } catch (e) {}
      }

      // Restart VAD
      if (this.dynamicSuppressionEnabled) {
        this.startVadLogic();
      }

      // Re-apply suppression
      this.setSuppressionLevel(this.suppressionLevel);

      console.log(`[DeepFilterProcessor] Unmuted — connections restored in ${(performance.now() - t0).toFixed(1)}ms`);
    }
  }

  async enableProcessing(enabled: boolean): Promise<void> {
    if (!this.deepFilterNet3) return;

    // Check API availability
    if (typeof this.deepFilterNet3.setNoiseSuppressionEnabled === 'function') {
      this.deepFilterNet3.setNoiseSuppressionEnabled(enabled);
    } else {
      console.warn('setNoiseSuppressionEnabled not available on DeepFilterNet3Processor');
    }

    this.isProcessing = enabled;
  }

  setSuppressionLevel(level: number): void {
    this.suppressionLevel = level;
    if (!this.deepFilterNet3) return;

    // Trigger VAD worker update if running
    if (this.vadWorker) {
      this.vadWorker.postMessage({
        type: 'SET_PARAMS',
        maxLevel: level,
      });
    }

    if (!this.dynamicSuppressionEnabled) {
      this.applyInternalSuppression(level);
    }
  }

  setDynamicSuppression(enabled: boolean): void {
    this.dynamicSuppressionEnabled = enabled;

    if (enabled) {
      if (this.isProcessing && !this.isMuted && !this.vadWorker) {
        this.ensureVadNode();
        // Determine tap point
        if (this.smartMixingEnabled && this.postGainNode) {
          this.postGainNode.connect(this.vadAnalyser!);
        } else if (this.deepFilterWorkletNode) {
          this.deepFilterWorkletNode.connect(this.vadAnalyser!);
        }
        this.startVadLogic();
      }
    } else {
      this.stopVadLogic();
      this.applyInternalSuppression(this.suppressionLevel);
    }
  }

  setSmartMixing(enabled: boolean): void {
    if (this.smartMixingEnabled === enabled) return;
    this.smartMixingEnabled = enabled;

    // Disable individual components when disabled
    if (!enabled) {
      this.preGainEnabled = false;
      this.eqEnabled = false;
      this.compressorEnabled = false;
      this.gateEnabled = false;
    }

    // Rebuild graph if running
    if (this.isProcessing && this.currentStream) {
      const track = this.currentStream.getAudioTracks()[0];
      if (track) {
        this.setupAudioGraph(track).catch((err) =>
          console.error('[DeepFilterProcessor] Failed to switch Smart Mixing mode:', err)
        );
      }
    }
  }

  // Individual Smart Mixing component setters
  setPreGainEnabled(enabled: boolean): void {
    this.preGainEnabled = enabled;
    if (this.preGainNode) {
      this.preGainNode.gain.value = enabled ? 1.1 : 1.0;
    }
  }

  setEQEnabled(enabled: boolean): void {
    this.eqEnabled = enabled;
    if (this.eqNode) {
      this.eqNode.gain.value = enabled ? this.AUDIO_QUALITY_CONFIG.EQ_GAIN : 0.0;
    }
  }

  setCompressorEnabled(enabled: boolean): void {
    this.compressorEnabled = enabled;
    if (this.compressorNode) {
      this.compressorNode.threshold.value = enabled ? this.AUDIO_QUALITY_CONFIG.COMPRESSOR_THRESHOLD : 0;
    }
  }

  setGateEnabled(enabled: boolean): void {
    this.gateEnabled = enabled;
    if (this.gateWorkletNode) {
      const bypassParam = (this.gateWorkletNode.parameters as any).get('bypass');
      if (bypassParam) {
        bypassParam.setValueAtTime(enabled ? 0 : 1, this.audioContext!.currentTime);
      }
    }
  }

  setPostGainEnabled(enabled: boolean): void {
    this.postGainEnabled = enabled;
    if (this.postGainNode && this.audioContext) {
      this.postGainNode.gain.cancelScheduledValues(this.audioContext.currentTime);
      this.postGainNode.gain.setTargetAtTime(
        enabled ? this.AUDIO_QUALITY_CONFIG.POST_GAIN_FACTOR : 1.0,
        this.audioContext.currentTime,
        0.02
      );
    }
  }

  getSmartMixingOptions() {
    return {
      smartMixing: this.smartMixingEnabled,
      preGain: this.preGainEnabled,
      eq: this.eqEnabled,
      compressor: this.compressorEnabled,
      gate: this.gateEnabled,
      postGain: this.postGainEnabled,
    };
  }

  get node(): AudioWorkletNode | null {
    return this.deepFilterWorkletNode;
  }

  stop(): void {
    this.isProcessing = false;
    this.disconnectNodes();
    // Revert nodes to null/initial state if needed, but disconnectNodes handles disconnection.
  }

  onMetrics(callback: (metrics: Partial<AudioQualityMetrics>) => void): void {
    this.metricsCallbacks.add(callback);
  }

  destroy(): void {
    this.stop();
    if (this.deepFilterNet3) {
      this.deepFilterNet3.destroy();
      this.deepFilterNet3 = null;
    }
    this.metricsCallbacks.clear();
  }
}
